/**
 * Confirmation Dialogs for PatrolModal
 *
 * Contains all confirmation/warning dialogs used in the patrol creation/editing flow.
 */

import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CompressIcon from '@mui/icons-material/Compress';
import moment from 'moment';

/**
 * Unsaved Changes Dialog
 */
export const UnsavedChangesDialog = memo(({ open, onContinueEditing, onDiscard }) => (
  <Dialog
    open={open}
    onClose={onContinueEditing}
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Unsaved Changes
      </Typography>
    </DialogTitle>
    <DialogContent>
      <Typography color="text.secondary">
        You have unsaved changes. Are you sure you want to close without saving?
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onContinueEditing} variant="outlined">
        Continue Editing
      </Button>
      <Button onClick={onDiscard} color="error" variant="contained">
        Discard Changes
      </Button>
    </DialogActions>
  </Dialog>
));

UnsavedChangesDialog.displayName = 'UnsavedChangesDialog';

UnsavedChangesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onContinueEditing: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
};

/**
 * Split Shift Warning Dialog
 */
export const SplitShiftWarningDialog = memo(({ open, gapHours, onCancel, onConfirm }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon sx={{ color: 'warning.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Split Shift Warning
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Typography color="text.secondary">
        You are creating a <strong>Split Shift</strong> with more than {Math.floor(gapHours)} hour
        {Math.floor(gapHours) !== 1 ? 's' : ''} break between patrols.
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        Are you sure you want to continue?
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} color="inherit">
        Cancel
      </Button>
      <Button onClick={onConfirm} color="warning" variant="contained">
        Create Split Shift
      </Button>
    </DialogActions>
  </Dialog>
));

SplitShiftWarningDialog.displayName = 'SplitShiftWarningDialog';

SplitShiftWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  gapHours: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

/**
 * Long Shift Warning Dialog
 */
export const LongShiftWarningDialog = memo(({ open, thresholdHours, onCancel, onConfirm }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon sx={{ color: 'warning.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Long Shift Warning
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Typography color="text.secondary">
        Adding this patrol will make the total shift length exceed <strong>{thresholdHours} hours</strong>.
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        Are you sure you want to continue?
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} color="inherit">
        Cancel
      </Button>
      <Button onClick={onConfirm} color="warning" variant="contained">
        Create Long Shift
      </Button>
    </DialogActions>
  </Dialog>
));

LongShiftWarningDialog.displayName = 'LongShiftWarningDialog';

LongShiftWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  thresholdHours: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

/**
 * Conflict Warning Dialog
 */
export const ConflictWarningDialog = memo(({ open, conflictSummary, onCancel, onConfirm }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorOutlineIcon sx={{ color: 'error.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Scheduling Conflict Detected
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        This patrol overlaps with an existing schedule for the same guard:
      </Typography>
      {conflictSummary && conflictSummary.conflicts && (
        <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, py: 0 }}>
          {conflictSummary.conflicts.map((conflict, index) => (
            <ListItem key={index} sx={{ py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={conflict.conflictingPatrol?.AddressName}
                secondary={`${moment(conflict.conflictingPatrol?.StartTime).format('ddd, MMM D')} at ${moment(conflict.conflictingPatrol?.StartTime).format('h:mm A')} - ${moment(conflict.conflictingPatrol?.EndTime).format('h:mm A')}`}
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                secondaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </ListItem>
          ))}
        </List>
      )}
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        Do you want to save anyway? This may result in the guard being double-booked.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} variant="outlined">
        Cancel
      </Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Save Anyway
      </Button>
    </DialogActions>
  </Dialog>
));

ConflictWarningDialog.displayName = 'ConflictWarningDialog';

ConflictWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  conflictSummary: PropTypes.shape({
    conflicts: PropTypes.arrayOf(
      PropTypes.shape({
        conflictingPatrol: PropTypes.shape({
          AddressName: PropTypes.string,
          StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
          EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        }),
      })
    ),
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

/**
 * Delete Patrol Confirmation Dialog
 */
export const DeletePatrolDialog = memo(({ open, onCancel, onConfirm }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeleteIcon sx={{ color: 'error.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Delete Patrol
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Typography color="text.secondary">
        Are you sure you want to delete this patrol? This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} variant="outlined">
        Cancel
      </Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Delete Patrol
      </Button>
    </DialogActions>
  </Dialog>
));

DeletePatrolDialog.displayName = 'DeletePatrolDialog';

DeletePatrolDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

/**
 * Validation Error Dialog
 */
export const ValidationErrorDialog = memo(({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon sx={{ color: 'error.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Missing Information
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Typography color="text.secondary">
        Please fill in all required fields before saving.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} color="primary" variant="contained">
        OK
      </Button>
    </DialogActions>
  </Dialog>
));

ValidationErrorDialog.displayName = 'ValidationErrorDialog';

ValidationErrorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Duplicate Week Confirmation Dialog
 */
export const DuplicateWeekConfirmationDialog = memo(({
  open,
  patrolCount,
  uniqueGuards,
  sourceWeekLabel,
  targetWeekLabel,
  onCancel,
  onConfirm,
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContentCopyIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Duplicate Week
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        <strong>{patrolCount} patrol{patrolCount !== 1 ? 's' : ''}</strong> will be duplicated
        from <strong>{sourceWeekLabel}</strong> to <strong>{targetWeekLabel}</strong>.
      </Typography>
      {uniqueGuards && uniqueGuards.length > 0 && (
        <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, py: 0 }}>
          {uniqueGuards.map((guard, index) => (
            <ListItem key={index} sx={{ py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={guard}
                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onCancel} variant="outlined">
        Cancel
      </Button>
      <Button onClick={onConfirm} color="primary" variant="contained">
        Duplicate {patrolCount} Patrol{patrolCount !== 1 ? 's' : ''}
      </Button>
    </DialogActions>
  </Dialog>
));

DuplicateWeekConfirmationDialog.displayName = 'DuplicateWeekConfirmationDialog';

DuplicateWeekConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  patrolCount: PropTypes.number.isRequired,
  uniqueGuards: PropTypes.arrayOf(PropTypes.string).isRequired,
  sourceWeekLabel: PropTypes.string.isRequired,
  targetWeekLabel: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

/**
 * Shift Overlap Dialog
 *
 * Shown when an edited patrol overlaps with the next patrol in the same shift.
 * Offers to push back subsequent patrols with a toggle for preserving or removing gaps.
 */
export const ShiftOverlapDialog = ({ open, editedPatrolLocation, affectedPatrols, overlapMinutes, onDecline, onConfirm }) => {
  const [preserveGaps, setPreserveGaps] = useState(false);

  const handleToggle = (_, newValue) => {
    if (newValue !== null) {
      setPreserveGaps(newValue);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onDecline}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: 'warning.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Shift Overlap Detected
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          The edited patrol at <strong>{editedPatrolLocation}</strong> now overlaps with the next patrol
          by <strong>{overlapMinutes} minute{overlapMinutes !== 1 ? 's' : ''}</strong>.
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Would you like to push back the following patrol{affectedPatrols && affectedPatrols.length !== 1 ? 's' : ''}?
        </Typography>
        {affectedPatrols && affectedPatrols.length > 0 && (
          <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, py: 0, mb: 2 }}>
            {affectedPatrols.map((patrol, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AccessTimeIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={patrol.AddressName}
                  secondary={`${moment(patrol.StartTime).format('h:mm A')} - ${moment(patrol.EndTime).format('h:mm A')}`}
                  primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </ListItem>
            ))}
          </List>
        )}
        <Box sx={{ mb: 1 }}>
          <ToggleButtonGroup
            value={preserveGaps}
            exclusive
            onChange={handleToggle}
            size="small"
            fullWidth
          >
            <ToggleButton value={false}>
              <CompressIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Remove Gaps
            </ToggleButton>
            <ToggleButton value={true}>
              <AccessTimeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Keep Gaps
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {preserveGaps
              ? 'All subsequent patrols will shift, preserving the original break times between them.'
              : 'Subsequent patrols will move just enough to clear the overlap. Gaps are removed.'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onDecline} variant="outlined">
          No, Keep Overlapping
        </Button>
        <Button onClick={() => onConfirm(preserveGaps)} color="primary" variant="contained">
          Yes, Push Back Patrols
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ShiftOverlapDialog.displayName = 'ShiftOverlapDialog';

ShiftOverlapDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  editedPatrolLocation: PropTypes.string,
  affectedPatrols: PropTypes.arrayOf(
    PropTypes.shape({
      AddressName: PropTypes.string,
      StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    })
  ),
  overlapMinutes: PropTypes.number,
  onDecline: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
